import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shirt, Cog, Zap, Building2, Truck, ShieldCheck, Wrench, Handshake, CheckCircle2 } from "lucide-react";
import SEO, { JsonLd } from "../components/SEO";
import Reveal from "../components/Reveal";
import { useI18n, STR } from "../i18n";
import { COMPANY, IMAGES, CATEGORIES, SERVICES, STATS } from "../mock";

const ICONS = { Shirt, Cog, Zap, Building2, Truck, ShieldCheck, Wrench, Handshake };

export default function Home() {
  const { t } = useI18n();
  const orgLd = {
    "@context": "https://schema.org", "@type": "Organization",
    name: COMPANY.name, url: "https://altamiravenezuela.com",
    logo: "https://altamiravenezuela.com/logo.png",
    address: { "@type": "PostalAddress", streetAddress: COMPANY.address, addressCountry: "VE" },
    contactPoint: COMPANY.phones.map(p => ({ "@type": "ContactPoint", telephone: p.tel, contactType: "sales", areaServed: p.flag })),
    sameAs: Object.values(COMPANY.social),
  };

  return (
    <>
      <SEO
        title={{ es: "Suministro industrial y petrolero — Venezuela, Colombia, EE. UU.", en: "Industrial & oilfield supply — Venezuela, Colombia, U.S." }}
        description={{ es: "Equipos de bombeo, válvulas y cabezales de pozo, transformadores, torres de transmisión y EPP. Cotización rápida por WhatsApp y correo.", en: "Pumping equipment, valves and wellheads, transformers, transmission towers and PPE. Fast quote via WhatsApp and email." }}
      />
      <JsonLd data={orgLd} />

      {/* Hero */}
      <section className="hero">
        <div className="bg" style={{ backgroundImage: `url(${IMAGES.heroMain})` }} />
        <div className="shade" />
        <div className="container-x inner">
          <span className="eyebrow">{t({ es: "Suministro industrial y petrolero", en: "Industrial & oilfield supply" })}</span>
          <h1>{t({ es: "Equipos, repuestos y logística para su operación en Venezuela, Colombia y EE. UU.", en: "Equipment, parts and logistics for your operation in Venezuela, Colombia and the U.S." })}</h1>
          <p className="lead">{t({ es: "Más de 15 años conectando a operadoras y contratistas con fabricantes de primer nivel. Cotización en menos de 24h.", en: "Over 15 years connecting operators and contractors with top-tier manufacturers. Quotes within 24 hours." })}</p>
          <div style={{ display: "flex", gap: 12, marginTop: 26, flexWrap: "wrap" }}>
            <Link to="/contacto" className="btn btn-gold">{t(STR.cta.quote)} <ArrowRight size={16} /></Link>
            <Link to="/catalogo" className="btn btn-ghost">{t(STR.cta.viewCatalog)}</Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section">
        <div className="container-x">
          <Reveal className="sh">
            <span className="eyebrow">{t({ es: "Líneas de negocio", en: "Business lines" })}</span>
            <h2>{t({ es: "Tres líneas de negocio, un solo aliado", en: "Three business lines, one partner" })}</h2>
            <p>{t({ es: "Oil & Gas, energía e infraestructura y protección personal, con respaldo técnico en sitio.", en: "Oil & Gas, power & infrastructure and personal protection, with on-site technical support." })}</p>
          </Reveal>
          <div className="grid-4">
            {CATEGORIES.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <Reveal key={c.id} delay={i * 80}>
                  <Link to={`/catalogo#${c.id}`} className="card" style={{ display: "block", height: "100%" }}>
                    <div className="img" style={{ backgroundImage: `url(${c.image})` }} />
                    <div className="body">
                      <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--sand)", display: "grid", placeItems: "center", color: "var(--navy)", marginBottom: 8 }}><Icon size={20} /></div>
                      <h3>{t(c.title)}</h3>
                      <p>{t(c.short)}</p>
                      <span style={{ color: "var(--navy)", fontWeight: 700, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>{t(STR.cta.viewMore)} <ArrowRight size={14} style={{ display: "inline", verticalAlign: "-2px" }} /></span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="section" style={{ background: "var(--sand)" }}>
        <div className="container-x" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }}>
          <Reveal>
            <div style={{ height: 420, backgroundImage: `url(${IMAGES.heroCompany})`, backgroundSize: "cover", backgroundPosition: "center", borderRadius: 10 }} />
          </Reveal>
          <Reveal delay={120}>
            <span className="eyebrow">{t({ es: "Sobre nosotros", en: "About us" })}</span>
            <h2 style={{ fontSize: 36, marginTop: 0 }}>{t({ es: "Un aliado binacional para su cadena de suministro", en: "A binational partner for your supply chain" })}</h2>
            <p style={{ color: "#475066", fontSize: 16, lineHeight: 1.7 }}>{t({ es: "Somos una empresa venezolana con presencia comercial en Colombia y Estados Unidos. Combinamos experiencia técnica, red de fabricantes y logística ágil para entregar equipos y repuestos en el sitio de operación, con documentación completa.", en: "We are a Venezuelan company with commercial presence in Colombia and the United States. We combine technical expertise, manufacturer network and agile logistics to deliver equipment and parts to the operation site, with full documentation." })}</p>
            <ul style={{ listStyle: "none", padding: 0, marginTop: 20 }}>
              {[{ es: "Cobertura en 3 países", en: "Coverage across 3 countries" }, { es: "Marcas OEM y equivalentes certificados", en: "OEM brands and certified equivalents" }, { es: "Logística puerta a puerta", en: "Door-to-door logistics" }].map((it, i) => (
                <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", color: "var(--navy)", fontWeight: 600 }}>
                  <CheckCircle2 size={18} style={{ color: "var(--gold)" }} /> {t(it)}
                </li>
              ))}
            </ul>
            <Link to="/empresa" className="btn btn-navy" style={{ marginTop: 24 }}>{t({ es: "Conocer la empresa", en: "Learn about us" })} <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section className="section">
        <div className="container-x">
          <div className="stats">
            {STATS.map((s, i) => (
              <Reveal key={i} delay={i * 80} className="stat">
                <div className="n">{s.n}</div>
                <div className="l">{t(s.label)}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section" style={{ background: "var(--sand)" }}>
        <div className="container-x">
          <Reveal className="sh">
            <span className="eyebrow">{t({ es: "Servicios", en: "Services" })}</span>
            <h2>{t({ es: "Más que un suministrador", en: "More than a supplier" })}</h2>
          </Reveal>
          <div className="grid-4">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={i} delay={i * 80}>
                  <div className="card" style={{ padding: 26 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, background: "var(--navy)", display: "grid", placeItems: "center", color: "var(--gold)", marginBottom: 14 }}><Icon size={22} /></div>
                    <h3 style={{ marginTop: 0 }}>{t(s.title)}</h3>
                    <p style={{ margin: 0, color: "#475066" }}>{t(s.desc)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctaband">
        <div className="container-x" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 34 }}>{t({ es: "¿Listo para su próxima orden?", en: "Ready for your next order?" })}</h2>
            <p style={{ margin: "8px 0 0", opacity: 0.85 }}>{t({ es: "Envíenos su lista y recibirá cotización formal en 24h.", en: "Send us your list and receive a formal quote within 24 hours." })}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/contacto" className="btn btn-gold">{t(STR.cta.quote)}</Link>
              <a href={`https://wa.me/${COMPANY.phones[0].wa}`} target="_blank" rel="noopener noreferrer" className="btn btn-wa">{t(STR.cta.whatsapp)}</a>
            </div>
            <div data-testid="cta-contact-person" style={{ textAlign: "center" }}>
              <img
                src="https://customer-assets-lxgj4vgw.emergentagent.net/job_search-rank-builder/artifacts/lw6nosxv_ChatGPT%20Image%20Sep%2028%2C%202026%2C%2004_53_05%20AM.png"
                alt="Nico Cubillos"
                style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", objectPosition: "top", border: "3px solid var(--gold)", display: "block", margin: "0 auto" }}
              />
              <div style={{ marginTop: 8, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 17, letterSpacing: "0.04em" }}>Nico Cubillos</div>
              <div data-testid="cta-contact-role" style={{ fontSize: 12, opacity: 0.75, marginTop: 2, letterSpacing: "0.03em" }}>{t({ es: "Director Comercial", en: "Commercial Director" })}</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
